<div class="company-payroll-admin" *ngIf="companyPayroll">
  <!-- Header -->
  <div class="header-section">
    <h2>{{companyPayroll.companyInfo?.name}} Payroll Administration</h2>
    <p class="subtitle">Manage and view payroll for all company representatives</p>
  </div>

  <!-- Summary Cards -->
  <div class="summary-grid">
    <nz-card class="metric-card">
      <nz-statistic 
        nzTitle="Total Paid" 
        [nzValue]="(companyPayroll.totalPaid | number)" 
        nzPrefix="$"
        [nzPrecision]="2">
      </nz-statistic>
      <p class="metric-subtitle">Net pay from paid cycles</p>
    </nz-card>
    <nz-card class="metric-card">
      <nz-statistic 
        nzTitle="Commissions Paid" 
        [nzValue]="(companyPayroll.totalCommissions | number)" 
        nzPrefix="$"
        [nzPrecision]="2">
      </nz-statistic>
      <p class="metric-subtitle">From paid cycles only</p>
    </nz-card>
    <nz-card class="metric-card">
      <nz-statistic 
        nzTitle="Holdbacks Paid" 
        [nzValue]="companyPayroll.totalHoldbacks | number" 
        nzPrefix="$"
        [nzPrecision]="2">
      </nz-statistic>
      <p class="metric-subtitle">From paid cycles only</p>
    </nz-card>
    <nz-card class="metric-card" style="margin-bottom: 24px;">
      <nz-statistic 
        nzTitle="Adjustments Paid" 
        [nzValue]="companyPayroll.totalAdjustments | number" 
        nzPrefix="$"
        [nzPrecision]="2">
      </nz-statistic>
      <p class="metric-subtitle">From paid cycles only</p>
    </nz-card>
  </div>

  <!-- Filters -->
  <nz-card class="filters-section">
    <div class="filters">
      <nz-range-picker 
        [(ngModel)]="dateRange" 
        (ngModelChange)="applyFilters()"></nz-range-picker>

      
      <nz-select 
        [(ngModel)]="selectedUserId" 
        nzPlaceHolder="Filter by Sales Rep" 
        (ngModelChange)="applyFilters()"
        nzAllowClear>
        <nz-option nzValue="" nzLabel="All Reps"></nz-option>
        <nz-option 
          *ngFor="let user of companyPayroll.companyUsers" 
          [nzValue]="user.userId" 
          [nzLabel]="user.first_name + ' ' + user.last_name">
        </nz-option>
      </nz-select>
      
      <button nz-button (click)="clearFilters()">
        <i nz-icon nzType="clear"></i>
        Clear Filters
      </button>
      
      <button nz-button nzType="primary" (click)="exportToCSV()" [disabled]="!selectedCycleDetails">
        <i nz-icon nzType="download"></i>
        Export Selected Cycle
      </button>
    </div>
  </nz-card>

  <!-- Payroll Cycles - Desktop Table View -->
  <nz-card nzTitle="Payroll Cycles" class="desktop-table">
    <div class="table-wrap">
      <nz-table 
        [nzData]="companyPayroll.payrollCycles" 
        [nzLoading]="loading"
        [nzPageSize]="10"
        nzShowPagination="true"
        [nzScroll]="{ x: '900px' }">
        <thead>
          <tr>
            <th nzLeft nzWidth="60px"></th>
            <th nzLeft nzWidth="100px">Pay Date</th>
            <th nzWidth="180px">Period</th>
            <th nzWidth="140px">Total Commissions</th>
            <th nzWidth="110px">Holdbacks</th>
            <th nzWidth="120px">Adjustments</th>
            <th nzWidth="110px">Net Pay</th>
            <th nzWidth="100px">Reps Paid</th>
            <th nzWidth="90px">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let cycle of companyPayroll.payrollCycles; trackBy: trackByCycleId">
            <td nzLeft>
              <button 
                nz-button 
                nzType="link" 
                nzSize="small"
                (click)="loadCycleDetails(cycle.id)"
                [nzLoading]="cycleDetailsLoading && selectedCycleId === cycle.id">
                <i nz-icon [nzType]="isCycleExpanded(cycle.id) ? 'minus' : 'plus'"></i>
              </button>
            </td>
            <td nzLeft>{{cycle.payDate | date:'shortDate'}}</td>
            <td>{{cycle.startDate | date:'shortDate'}} - {{cycle.endDate | date:'shortDate'}}</td>
            <td>{{formatCurrency(cycle.totalCommissions)}}</td>
            <td>{{formatCurrency(cycle.totalHoldbacks)}}</td>
            <td>{{formatCurrency(cycle.totalAdjustments)}}</td>
            <td><strong>{{formatCurrency(cycle.netPay)}}</strong></td>
            <td>{{cycle.repsPaid}}</td>
            <td>
              <nz-tag [nzColor]="getStatusColor(cycle.status)">
                {{cycle.status | titlecase}}
              </nz-tag>
            </td>
          </tr>
        </tbody>
      </nz-table>
    </div>
  </nz-card>

  <!-- Payroll Cycles - Mobile Card View -->
  <div class="mobile-cards">
    <h3 class="mobile-section-title">Payroll Cycles</h3>
    <div class="cycle-card" *ngFor="let cycle of companyPayroll.payrollCycles | slice:0:10; trackBy: trackByCycleId">
      <div class="card-header" (click)="toggleCycleCardExpansion(cycle.id)">
        <div class="card-header-left">
          <div class="card-date">{{cycle.payDate | date:'MMM d, yyyy'}}</div>
          <div class="card-period">{{cycle.startDate | date:'MMM d'}} - {{cycle.endDate | date:'MMM d, yyyy'}}</div>
        </div>
        <div class="card-header-right">
          <div class="card-amount">{{formatCurrency(cycle.netPay)}}</div>
          <div class="card-header-actions">
            <nz-tag [nzColor]="getStatusColor(cycle.status)" class="card-status">
              {{cycle.status | titlecase}}
            </nz-tag>
            <i nz-icon [nzType]="isCycleCardExpanded(cycle.id) ? 'up' : 'down'" class="expand-icon"></i>
          </div>
        </div>
      </div>
      <div class="card-details" *ngIf="isCycleCardExpanded(cycle.id)">
        <div class="detail-row">
          <span class="detail-label">Total Commissions</span>
          <span class="detail-value">{{formatCurrency(cycle.totalCommissions)}}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Holdbacks</span>
          <span class="detail-value">{{formatCurrency(cycle.totalHoldbacks)}}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Adjustments</span>
          <span class="detail-value">{{formatCurrency(cycle.totalAdjustments)}}</span>
        </div>
        <div class="detail-row highlight">
          <span class="detail-label">Net Pay</span>
          <span class="detail-value"><strong>{{formatCurrency(cycle.netPay)}}</strong></span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Reps Paid</span>
          <span class="detail-value">{{cycle.repsPaid}}</span>
        </div>
        <button 
          nz-button 
          nzType="default" 
          nzBlock
          class="view-details-btn"
          (click)="loadCycleDetails(cycle.id); $event.stopPropagation();"
          [nzLoading]="cycleDetailsLoading && selectedCycleId === cycle.id">
          <i nz-icon [nzType]="isCycleExpanded(cycle.id) ? 'up' : 'down'"></i>
          {{isCycleExpanded(cycle.id) ? 'Hide' : 'View'}} Sales Rep Details
        </button>
      </div>
    </div>
    <div class="mobile-pagination" *ngIf="companyPayroll.payrollCycles && companyPayroll.payrollCycles.length > 10">
      <span class="pagination-text">Showing first 10 of {{companyPayroll.payrollCycles.length}} cycles</span>
    </div>
  </div>

  <!-- Sales Rep Performance - Desktop Table View -->
  <nz-card nzTitle="Sales Rep Performance" *ngIf="selectedCycleDetails" class="desktop-table">
    <div class="table-wrap">
      <nz-table 
        [nzData]="selectedCycleDetails" 
        [nzLoading]="cycleDetailsLoading"
        [nzPageSize]="15"
        nzShowPagination="true"
        [nzScroll]="{ x: '800px' }">
        <thead>
          <tr>
            <th nzLeft nzWidth="180px">Sales Rep</th>
            <th nzWidth="110px">Total Customers</th>
            <th nzWidth="140px">Commissions</th>
            <th nzWidth="110px">Holdbacks</th>
            <th nzWidth="130px">Adjustments</th>
            <th nzWidth="130px">Net Pay</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let user of selectedCycleDetails; trackBy: trackByUserId">
            <td nzLeft>{{user.userName}}</td>
            <td>{{user.salesCount}}</td>
            <td>{{formatCurrency(user.totalCommissions)}}</td>
            <td>{{formatCurrency(user.totalHoldbacks)}}</td>
            <td>{{formatCurrency(user.totalAdjustments)}}</td>
            <td>
              <strong [style.color]="user.netPay >= 0 ? 'green' : 'red'">
                {{formatCurrency(user.netPay)}}
              </strong>
            </td>
          </tr>
        </tbody>
      </nz-table>
    </div>
  </nz-card>

  <!-- Sales Rep Performance - Mobile Card View -->
  <div *ngIf="selectedCycleDetails && selectedCycleDetails.length > 0" class="mobile-cards rep-cards">
    <h3 class="mobile-section-title">
      Sales Rep Performance
      <span class="rep-count">({{selectedCycleDetails.length}} reps)</span>
    </h3>
    <nz-spin [nzSpinning]="cycleDetailsLoading">
      <cdk-virtual-scroll-viewport itemSize="85" class="rep-scroll-viewport">
        <div class="rep-card" *cdkVirtualFor="let user of selectedCycleDetails; trackBy: trackByUserId">
          <div class="rep-header" (click)="toggleRepCardExpansion(user.userId)">
            <div class="rep-name">{{user.userName}}</div>
            <div class="rep-header-right">
              <div class="rep-net" [style.color]="user.netPay >= 0 ? '#22c55e' : '#ef4444'">
                {{formatCurrency(user.netPay)}}
              </div>
              <i nz-icon [nzType]="isRepCardExpanded(user.userId) ? 'up' : 'down'" class="expand-icon"></i>
            </div>
          </div>
          <div class="rep-details" *ngIf="isRepCardExpanded(user.userId)">
            <div class="detail-row">
              <span class="detail-label">Total Customers</span>
              <span class="detail-value">{{user.salesCount}}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Commissions</span>
              <span class="detail-value">{{formatCurrency(user.totalCommissions)}}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Holdbacks</span>
              <span class="detail-value">{{formatCurrency(user.totalHoldbacks)}}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Adjustments</span>
              <span class="detail-value">{{formatCurrency(user.totalAdjustments)}}</span>
            </div>
          </div>
        </div>
      </cdk-virtual-scroll-viewport>
    </nz-spin>
  </div>
</div>

<!-- Loading State -->
<div class="loading-container" *ngIf="loading">
  <nz-spin nzSize="large">
    <div class="loading-text">Loading company payroll data...</div>
  </nz-spin>
</div>

<!-- Empty State -->
<div class="empty-state" *ngIf="!loading && !companyPayroll">
  <nz-empty 
    nzNotFoundImage="simple" 
    nzNotFoundContent="No payroll data available for your company">
    <button nz-button nzType="primary" (click)="loadCompanyPayrollData()">
      Refresh Data
    </button>
  </nz-empty>
</div>
